
import { type Context } from "@nuxt/types";
import {
  ComponentOptions,
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  ref,
  useMeta,
  useRoute,
  useRouter,
  useStore,
  watch,
} from "@nuxtjs/composition-api";
import DOMPurify from "isomorphic-dompurify";
import { Vue } from "nuxt-property-decorator";
import { type NavigationGuardNext, type Route } from "vue-router";
import { onBeforeRouteLeave } from "vue-router/composables";

import LtkCoachmark from "@/components/Ltk/LtkCoachmark.vue";
import { useAmplitude, useBranchService, useConfig, useMetaService, useTranslate, useVuetify } from "@/composables";
import { useCreatorShopSearchPosts, useCreatorShopSearchProducts } from "@/composables/creator-shop";
import { getCollectionsStore } from "@/store/collections";
import { useCreatorProductStore } from "@/store/creator-products";
import { useCreatorShop } from "@/store/creator-shop";
import { getExperimentsStore } from "@/store/experiments";
import { getGiftGuidesStore } from "@/store/gift-guides";
import { getLtksStore } from "@/store/ltks";
import { getProductSetsStore } from "@/store/product-sets";
import { getProfilesStore } from "@/store/profiles";
import { getTrendingPostsStore } from "@/store/trending-posts";
import { formatErrorMessage, handleErrorStatusCode } from "@/utils/api";
import { encode as asciiEncode } from "@/utils/encoding/ascii";
import { encode as htmlEncode } from "@/utils/encoding/html";
import { FOUND, NOT_FOUND } from "@/utils/enums/http-codes";
import { time } from "@/utils/enums/time";
import {
  EXPERIMENTS_CREATOR_FOLLOW_VPM,
  EXPERIMENTS_CREATOR_GIFT_GUIDES,
  EXPERIMENTS_TRENDING_POST_CAROUSEL,
  FEATURE_FLAG_ACTIVE,
  FLAG_CREATOR_SHOP_SEARCH,
  VARIANT_TREATMENT,
} from "@/utils/experiments/constants";
import { COACHMARK_CREATOR_PAGE } from "@/utils/localstorage/constants";
import logger, { logLevel } from "@/utils/logger";
import { TOOLTIP_REFERRER_DISCOVER_PAGE_FOLLOW_TIMESTAMP } from "@/utils/sessionstorage/constants";

export default defineComponent({
  name: "Profile",
  setup() {
    const amplitude = useAmplitude();
    const branch = useBranchService();
    const config = useConfig();
    const metaService = useMetaService();
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const { t } = useTranslate();
    const vuetify = useVuetify();

    const collectionsStore = getCollectionsStore(store);
    const creatorProductStore = useCreatorProductStore();
    const creatorShop = useCreatorShop();
    const experimentsStore = getExperimentsStore(store);
    const productSetStore = getProductSetsStore(store);
    const profilesStore = getProfilesStore(store);
    const trendingPostsStore = getTrendingPostsStore(store);
    const ltkStore = getLtksStore(store);

    const searchTerm = ref("");
    const selectedTab = ref("posts");
    const coachmarkTimeoutId = ref<number | null>(null);

    const displayName = computed(() => route.value.params.displayName);
    const profileId = computed(() => profilesStore.getIdByDisplayName(displayName.value) ?? "");
    const profile = computed(() => profilesStore.profiles[profileId.value]);
    const isCreatorShopSearchEnabled = computed(
      () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
    );

    const hasPinnedPosts = computed(() => {
      const postFeed = ltkStore.postFeed(profileId.value);
      return postFeed?.ids.some((id) => Boolean(ltkStore.posts[id]?.pinnedAt));
    });

    const {
      legacyLtksSearchFeed,
      ltksSearchFeed,
      showLoading: showPostsLoading,
      showError: showPostsError,
      onSearchPosts,
      onSearchMorePosts,
      resetPagination: resetPostsPagination,
      resetSearch: resetPostsSearch,
    } = useCreatorShopSearchPosts(profileId.value);
    const {
      legacyProductSearchFeed,
      productSearchFeed,
      showLoading: showProductsLoading,
      showError: showProductsError,
      onSearchProducts,
      onSearchMoreProducts,
      resetPagination: resetProductsPagination,
      resetSearch: resetProductsSearch,
    } = useCreatorShopSearchProducts(profileId.value);

    const ltkIds = computed<string[]>(() => {
      const searchTermValue = searchTerm.value;
      const isCreatorShopSearchEnabledValue = isCreatorShopSearchEnabled.value;
      if (searchTermValue) {
        if (isCreatorShopSearchEnabledValue) {
          return ltksSearchFeed.value[searchTermValue]?.items.map(({ id }) => id) ?? [];
        } else {
          return legacyLtksSearchFeed.value[searchTermValue]?.ids ?? [];
        }
      }
      return ltkStore.postFeed(profileId.value)?.ids ?? [];
    });

    const productIds = computed<string[]>(() => {
      const searchTermValue = searchTerm.value;
      const isCreatorShopSearchEnabledValue = isCreatorShopSearchEnabled.value;
      if (searchTermValue) {
        if (isCreatorShopSearchEnabledValue) {
          return productSearchFeed.value[searchTermValue]?.items.map(({ id }) => id) ?? [];
        } else {
          return legacyProductSearchFeed.value[searchTermValue]?.ids ?? [];
        }
      }
      return creatorProductStore.recentProductsFeed[profileId.value]?.ids ?? [];
    });

    const isSearch = computed(() => Boolean(searchTerm.value));

    const willSeeTrendingPosts = computed(() => {
      const minTrendingPosts = 3;
      const experiment = experimentsStore.flag(EXPERIMENTS_TRENDING_POST_CAROUSEL);
      const displayNameValue = displayName.value;

      if (experiment.value !== "on") {
        return false;
      }
      return trendingPostsStore.feeds[displayNameValue]?.ids.length >= minTrendingPosts;
    });

    const willSeeTrendingProducts = computed(() => {
      return Boolean(creatorProductStore.trendingProductsFeed[profileId.value]?.ids.length);
    });

    const hasProductsSets = computed(() => {
      return productSetStore.feeds[displayName.value]?.ids.length > 0;
    });

    const hasCollections = computed(() => {
      return collectionsStore.creatorCollectionFeed(displayName.value).ids.length > 0;
    });

    const postsTabText = computed(() => {
      return t("Posts").value;
    });

    const productsTabText = computed(() => {
      return t("Products").value;
    });

    const isFollowButtonVisible = computed(() => {
      if (process.client) {
        const MIN_VISIBLE_TOP_POSITION = 75;
        const followButton = document.querySelector("#coachmarkTarget");
        // We assume the DOM element is visible before destructuring
        const { top, left, bottom, right } = (followButton as HTMLElement)?.getBoundingClientRect() || {};

        return (
          top >= MIN_VISIBLE_TOP_POSITION &&
          left >= 0 &&
          bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          right <= (window.innerWidth || document.documentElement.clientWidth)
        );
      } else {
        return false;
      }
    });

    const isEligibleForCoachmark = computed(() => {
      return (
        vuetify.breakpoint.xsOnly &&
        !hasAlreadySeenCoachmark() &&
        !branch.isJourneyDisplayed() &&
        isFollowButtonVisible.value
      );
    });

    const coachmarkText = computed(() => {
      return t("Tap the follow button to keep up with styles from your go-to Creators and see exclusive sales first.")
        .value;
    });

    const pageTitle = computed(() => {
      return t("{influencerName} on LTK", { influencerName: displayName.value }).value;
    });

    const hasAlreadySeenCoachmark = () => {
      return process.client ? localStorage.getItem(COACHMARK_CREATOR_PAGE) === "true" : false;
    };

    const initFollowButtonCoachmark = () => {
      const coachmarkMount = document.querySelector("#coachmarkMount");
      const followButton = document.querySelector("#coachmarkTarget");
      // mount coachmark, and push button through overlay

      const Coachmark = Vue.extend(LtkCoachmark as ComponentOptions<Vue>);

      new Coachmark({
        propsData: {
          text: coachmarkText.value,
          top: "-42px",
          tailLeft: "60px",
          tailTop: "-12px",
          tailPointUp: true,
          dismissOnWindowClick: true,
          dismissOnWindowScroll: true,
          displayedInViewportDelay: time.ONE_SECOND,
          handleIsDisplayedInViewport: () => {
            try {
              localStorage.setItem(COACHMARK_CREATOR_PAGE, "true");
              // set value to enabled attribution to tooltip referrer
              sessionStorage.setItem(TOOLTIP_REFERRER_DISCOVER_PAGE_FOLLOW_TIMESTAMP, `${Date.now()}`);
            } catch (e) {
              console.error("There is an issue accessing local and/or session storage: ", e);
            }
            amplitude?.sendEvent("/web-vpm-displayed", {
              "vpm-type": "follow a creator",
            });
          },
        },
      }).$mount(coachmarkMount as HTMLElement);

      followButton?.setAttribute(
        "style",
        `z-index: ${window.getComputedStyle(document.body).getPropertyValue("--z-like-and-follow-button")}`,
      );
    };

    const isInTooltipExperimentGroup = (): boolean => {
      return experimentsStore.variant(EXPERIMENTS_CREATOR_FOLLOW_VPM).value === VARIANT_TREATMENT;
    };

    const setSelectedTabOnMount = () => {
      const defaultTab = "posts";
      const tab = route.value.query.tab;
      const sanitizedTab = DOMPurify.sanitize(Array.isArray(tab) ? tab[0] ?? "" : tab);
      const validTabs = ["posts", "products"];

      selectedTab.value = validTabs.includes(sanitizedTab) ? sanitizedTab : defaultTab;
    };

    const sendPagePublisherEvent = () => {
      // Prevent issues with DOM being unavailable for isEligibleForCoachmark check https://github.com/nuxt/nuxt/issues/13471
      requestAnimationFrame(() => {
        amplitude?.sendEvent("/page/publisher", {
          "publisher-id": profileId.value,
          "display-name": displayName.value,
          "creator-shop-trending-content-carousel-displayed": willSeeTrendingPosts.value ? "true" : null,
          "creator-shop-trending-products-carousel-displayed": willSeeTrendingProducts.value ? "true" : null,
          "has-collections": hasCollections.value,
          "has-sets": hasProductsSets.value,
          "has-pinned-posts": hasPinnedPosts.value,
          "creator-shop-tab": selectedTab.value,
        });
      });
    };

    const onSearch = async (query: string) => {
      resetPostsPagination();
      resetProductsPagination();
      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        await Promise.all([onSearchPosts(trimmedQuery), onSearchProducts(trimmedQuery)]);
        const isPostsTab = selectedTab.value === "posts";
        requestAnimationFrame(() => {
          amplitude?.sendEvent("/ltk-keyword-search-shop/search-results", {
            "is-redis": isCreatorShopSearchEnabled.value,
            "referring-component": isPostsTab ? "creator shop post feed" : "creator shop product feed",
            "creator-shop-tab": isPostsTab ? "posts" : "products",
            "result-count": isPostsTab ? ltkIds.value.length : productIds.value.length,
            "search-string": trimmedQuery,
          });
        });
      }
      searchTerm.value = trimmedQuery;
    };

    const onPagePosts = async () => {
      if (searchTerm.value) {
        await onSearchMorePosts(searchTerm.value);
      } else {
        await ltkStore.fetchPostsByProfileId({ profileId: profileId.value });
      }
    };

    const onRetryPosts = async () => {
      if (searchTerm.value) {
        await onSearchPosts(searchTerm.value);
      } else {
        await ltkStore.fetchPostsByProfileId({ profileId: profileId.value, overwrite: true });
      }
    };

    const onPageProducts = async () => {
      if (searchTerm.value) {
        await onSearchMoreProducts(searchTerm.value);
      } else {
        await creatorProductStore.fetchCreatorRecentProducts(profileId.value);
      }
    };

    watch(selectedTab, (newTab) => {
      if (process.client) {
        window.scrollTo({ top: 0 });
        if (newTab !== route.value.query.tab) {
          router.replace({ path: route.value.path, query: { tab: newTab } });
          sendPagePublisherEvent();
        }
        creatorShop.setLastSeenShopTab(newTab);
      }
    });

    onBeforeRouteLeave((to: Route, _from: Route, next: NavigationGuardNext) => {
      if (!to.name?.startsWith("explore-displayName")) {
        creatorShop.completeShopTabJourney();
      }
      next();
    });

    onMounted(() => {
      creatorShop.beginShopTabJourney();
      setSelectedTabOnMount();

      coachmarkTimeoutId.value = window.setTimeout(async () => {
        if (isEligibleForCoachmark.value) {
          if (await isInTooltipExperimentGroup()) {
            initFollowButtonCoachmark();
          }
        }
        // TS thinks setTimeout returns a NodeJS.Timeout object unsure why
        // possibly because the mounted hook runs client side instead of in Node
      }, time.TWO_SECONDS);

      if (process.client) {
        sendPagePublisherEvent();
      }
      creatorProductStore.fetchCreatorRecentProducts(profileId.value);
    });

    onBeforeUnmount(() => {
      if (coachmarkTimeoutId.value) {
        // If the coachmark timeout has an ID we should clear it
        // This prevents the coachmark from displaying after a route transition
        clearInterval(coachmarkTimeoutId.value);
      }
    });

    onUnmounted(() => {
      // We do not want to keep searches that are specific to a creator shop
      resetPostsSearch();
      resetProductsSearch();
    });

    useMeta(() => {
      const branchMeta = {
        title: t("Get my full shop experience").value,
        title_treatment: t("Get my full LTK experience").value,
        subtitle: t("Download the LTK app now to shop all of my favorite finds in one place.").value,
        subtitle_treatment: t("Download the LTK app to shop my favorites and get notified when I post fresh content.")
          .value,
        avatarUrl: profile.value?.avatarUrl,
        button: t("Get the free LTK app").value,
        button_treatment: t("Get the LTK app").value,
        getButton: t("GET").value,
        openButton: t("OPEN").value,
        page: "creator_shop",
        continue: t("Continue on web").value,
      };

      const description = t(
        "Shop the latest from {influencerName} on LTK, the easiest way to shop everything from your favorite influencers.",
        { influencerName: displayName.value },
      ).value;

      const head = metaService.setPageMeta({ title: pageTitle.value, customTitle: true, description, branchMeta });

      if (profile.value) {
        const height = 400;
        const width = 400;
        const image = `${profile.value.avatarUrl}?fit=crop&w=${width}&h=${height}`;

        head.meta?.push(...metaService.setOGImg(image, width));
      }

      const displayNameEncoded = asciiEncode(displayName.value);
      const bio = profile.value?.bio
        ? htmlEncode(profile.value?.bio)
        : "Shop all of my favorites right from my LTK shop!";
      const url = new URL(`https://${config.ltkSite}/`);
      url.pathname = `/explore/${displayName.value}`;
      const jsonld = {
        "@context": "http://schema.org/",
        "@type": "OnlineStore",
        name: `${displayNameEncoded}'s LTK Shop`,
        founder: displayNameEncoded,
        description: bio,
        image: profile.value?.avatarUrl ?? "",
        url: url.href,
      };

      head.script = [{ type: "application/ld+json", json: JSON.parse(JSON.stringify(jsonld)) }];

      return head;
    });

    return {
      displayName,
      profileId,
      profile,
      pageTitle,
      selectedTab,
      hasProductsSets,
      postsTabText,
      productsTabText,
      isSearch,
      ltkIds,
      showPostsLoading,
      showPostsError,
      productIds,
      showProductsLoading,
      showProductsError,
      onSearch,
      onPagePosts,
      onRetryPosts,
      onPageProducts,
    };
  },
  async asyncData({ params, error, store, redirect, $pinia }: Context) {
    const displayName = params.displayName;
    const profilesStore = getProfilesStore(store);
    const giftGuideStore = getGiftGuidesStore(store);
    const productSetStore = getProductSetsStore(store);
    const collectionsStore = getCollectionsStore(store);
    const experimentsStore = getExperimentsStore(store);
    const trendingPostStore = getTrendingPostsStore(store);
    const creatorProductStore = useCreatorProductStore($pinia); // this is necessary during SSR to ensure concurrent requests don't contaminate the store state (https://pinia.vuejs.org/core-concepts/outside-component-usage.html#SSR-Apps)
    const ltkStore = getLtksStore(store);

    try {
      // Ensure profile exists before rendering page
      await profilesStore.fetchProfileByDisplayName(displayName);
      const profileId = profilesStore.getIdByDisplayName(displayName);

      experimentsStore.fetchFeatureFlag({ flagName: EXPERIMENTS_CREATOR_GIFT_GUIDES });
      experimentsStore.fetchVariant({ variantName: EXPERIMENTS_CREATOR_FOLLOW_VPM });
      const giftGuideFF = experimentsStore.flag(EXPERIMENTS_CREATOR_GIFT_GUIDES).value === "on";

      if (!profileId) {
        error({ statusCode: NOT_FOUND, message: `Profile not found (${displayName})` });
        return params;
      }

      const profileDisplayName = profilesStore.profiles[profileId]?.displayName;
      if (profileDisplayName !== params.displayName) {
        redirect(FOUND, `/explore/${profileDisplayName}/`);
        return params;
      }

      // Batch additional API requests into a Promise.all, so they can run concurrently.
      const promises = [];

      if (!ltkStore.hasPostFeedBeenFetched(profileId)) {
        promises.push(ltkStore.fetchPostsByProfileId({ profileId }));
      }

      if (!trendingPostStore.hasFeedBeenFetched(displayName)) {
        promises.push(trendingPostStore.fetchTrendingPostsByDisplayName({ displayName }));
      }

      if (!collectionsStore.hasFeedBeenFetched(displayName)) {
        promises.push(collectionsStore.fetchCreatorCollectionFeed({ id: profileId, displayName }));
      }

      if (giftGuideFF) {
        if (!giftGuideStore.haveCategoriesBeenFetched) {
          promises.push(giftGuideStore.fetchLocalizedCategories());
        }
        if (giftGuideStore.feeds[displayName] === undefined) {
          promises.push(
            giftGuideStore.fetchGiftGuidesByProfile({
              displayName,
              profileId,
            }),
          );
        }
      }

      if (!productSetStore.hasFeedBeenFetched(displayName)) {
        promises.push(productSetStore.fetchProductSetsByProfile({ displayName, profileId, giftGuideFF }));
      }

      promises.push(creatorProductStore.fetchCreatorTrendingProducts(profileId));

      await Promise.allSettled(promises);
    } catch (err) {
      logger(logLevel.ERROR, `Error fetching on page /explore/displayName (${params.displayName})`, {}, err);
      error({
        statusCode: handleErrorStatusCode(err),
        message: `Error fetching on page /explore/displayName (${params.displayName}) - ${formatErrorMessage(err)}`,
      });
    }
    return params;
  },
  fetch() {
    const experimentsStore = getExperimentsStore(this.$store);
    experimentsStore.fetchFeatureFlag({ flagName: EXPERIMENTS_CREATOR_FOLLOW_VPM });
    experimentsStore.fetchFeatureFlag({ flagName: EXPERIMENTS_CREATOR_GIFT_GUIDES });
    experimentsStore.fetchFeatureFlag({ flagName: FLAG_CREATOR_SHOP_SEARCH });
  },
  head: {},
  fetchDelay: 0,
});
